import {
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Link,
  Typography,
  Box,
} from "@mui/material";
import Controls from "../../../../atoms/Controls";
import { WorkPlanDto } from "../../../../api-client";
import { useState, useMemo, useEffect } from "react";
import moment, { Moment } from "moment";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import EffectiveDateDialog from "./EffectiveDateDialog";
import { useLocations, useWorkPlans, useWeeklyWorkDaysByWorkPlan } from "../../../../stores/User/user.queries";
import {
  useCreateWorkPlan,
  useUpdateWorkPlan,
  useDeleteWorkPlan,
  useVersionWorkPlan,
} from "../../../../stores/User/user.mutations";
import { Delete, Add, Clear, Save, Edit, WarningAmber, ContentCopy, History } from "@mui/icons-material";
import FlatCard from "../../../../atoms/FlatCard";
import LoadingSpinner from "../../../BIDashboard/components/LoadingSpinner";

interface WorkPlansProps {
  user: any;
  selectedWorkPlanId?: number;
  onItemClick?: (workPlan: WorkPlanDto) => void;
  onDeleted?: (workPlan: WorkPlanDto) => void;
}

type ShadowWorkPlanDtoType = WorkPlanDto;

interface WorkPlanInclusive extends ShadowWorkPlanDtoType {
  validityEndInclusive: string | null;
}

type DatePickerChangeEvent = Date | { target: { value: string | null } } | null;

/**
 * How validity dates are shown in this table — and parsed back when a row is
 * edited. Spelled out rather than moment's locale-dependent "L", which is only
 * German while some other component has switched moment's global locale.
 */
const DISPLAY_DATE_FORMAT = "DD.MM.YYYY";

const workplanColumns = [
  { id: "id", label: "Id", minWidth: 50 },
  { id: "validityStart", label: "Gültig ab", minWidth: 50 },
  { id: "validityEnd", label: "Gültig bis", minWidth: 50 },
  { id: "location", label: "Standort", minWidth: 80 },
  {
    id: "actions",
    label: "Aktionen",
    minWidth: 50,
  },
];

const WorkPlans: React.FC<WorkPlansProps> = ({ user, onItemClick, onDeleted }) => {
  // React Query hooks
  const { data: workPlansData, isLoading: loading } = useWorkPlans(user.id);
  const createWorkPlanMutation = useCreateWorkPlan();
  const updateWorkPlanMutation = useUpdateWorkPlan();
  const deleteWorkPlanMutation = useDeleteWorkPlan();
  const versionWorkPlanMutation = useVersionWorkPlan();

  const [isAddingRow, setIsAddingRow] = useState(false);
  const [startDate, setStartDate] = useState<Moment | null>();
  const [endDate, setEndDate] = useState<Moment | null>();
  // The row the confirmation dialog acts on, held separately from the row that
  // is merely highlighted: deleting off `selectedWorkPlan` meant the dialog
  // targeted whatever was last clicked, which need not be the row whose delete
  // button was pressed.
  const [workPlanToDelete, setWorkPlanToDelete] = useState<WorkPlanInclusive | null>(null);
  // Work plan being duplicated (controls dialog visibility)
  const [workPlanToDuplicate, setWorkPlanToDuplicate] = useState<WorkPlanInclusive | null>(null);
  const [selectedWorkPlan, setSelectedWorkPlan] = useState<WorkPlanInclusive>();
  const [editingId, setEditingId] = useState<number | null>(null);
  // Whether to show expired (historical) work plans
  const [showExpired, setShowExpired] = useState(false);
  // Location of the row being added/edited. Work plans are location-specific;
  // the default is the employee's primary work location (therapistLocationId,
  // the admin-maintained Einsatzort — NOT locationId, which is only the
  // location last selected in the app header).
  const [planLocationId, setPlanLocationId] = useState<string | null>(null);

  // Fetch weekly work days for the plan being duplicated
  const { data: weeklyWorkDaysForDuplicate } = useWeeklyWorkDaysByWorkPlan(
    user.id,
    workPlanToDuplicate?.id ?? undefined
  );

  const { data: locations } = useLocations();
  const locationSelectOptions = useMemo(
    () =>
      locations?.map((location: any) => ({
        label: location.name,
        value: location.name?.toString(),
        id: location.id?.toString(),
      })) ?? [],
    [locations]
  );
  const locationName = (locationId: string | null | undefined) =>
    locations?.find((location) => location.id === locationId)?.name ?? locationId ?? "";

  // Row-level state addresses records owned by one user; drop it when the view
  // switches so nothing can be edited or deleted against the wrong account.
  useEffect(() => {
    setIsAddingRow(false);
    setStartDate(null);
    setEndDate(null);
    setWorkPlanToDelete(null);
    setWorkPlanToDuplicate(null);
    setSelectedWorkPlan(undefined);
    setEditingId(null);
    setPlanLocationId(null);
    setShowExpired(false);
  }, [user?.id]);

  const convertWPToInclusive = (workPlan: WorkPlanDto): WorkPlanInclusive => {
    const convertedWorkPlan = {
      ...workPlan,
      validityEndInclusive: workPlan.validityEnd
        ? moment(workPlan.validityEnd).subtract(1, "days").format(DISPLAY_DATE_FORMAT)
        : "unbefristet",
    };
    convertedWorkPlan.validityStart = moment(convertedWorkPlan.validityStart).format(DISPLAY_DATE_FORMAT);
    // console.log("convertWPToInclusive", convertedWorkPlan);
    return convertedWorkPlan;
  };
  const convertInclusiveToWP = (workPlanIncl: WorkPlanInclusive): WorkPlanDto => {
    const wp = {
      ...workPlanIncl,
      validityEnd: workPlanIncl.validityEndInclusive
        ? moment(workPlanIncl.validityEndInclusive)
            .add(1, "days")
            .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
            .format()
        : null,
    };
    wp.validityStart = moment(wp.validityStart)
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      .format();
    // console.log("convertInclusiveToWP", wp);
    return wp;
  };

  // Convert work plans to inclusive format
  const allWorkPlans = useMemo(() => {
    if (!workPlansData) return [];
    return workPlansData.map(convertWPToInclusive);
  }, [workPlansData]);

  // Separate active and expired plans
  const { activeWorkPlans, expiredWorkPlans } = useMemo(() => {
    const today = moment().startOf("day");
    const active: WorkPlanInclusive[] = [];
    const expired: WorkPlanInclusive[] = [];

    for (const plan of allWorkPlans) {
      // A plan is expired if validityEnd is set and is on or before today
      // (validityEnd is exclusive in the half-open interval [start, end),
      // so validityEnd = today means the last valid day was yesterday)
      const rawEnd = workPlansData?.find((p) => p.id === plan.id)?.validityEnd;
      if (rawEnd && moment(rawEnd).isSameOrBefore(today, "day")) {
        expired.push(plan);
      } else {
        active.push(plan);
      }
    }
    return { activeWorkPlans: active, expiredWorkPlans: expired };
  }, [allWorkPlans, workPlansData]);

  // Visible work plans based on showExpired toggle
  const workPlans = showExpired ? allWorkPlans : activeWorkPlans;

  // Ids of plans whose validity window overlaps another plan AT THE SAME
  // location (plans at different locations may overlap by design). Windows are
  // half-open — validityEnd is the exclusive boundary — so a plan starting
  // exactly on another's end is adjacent, not overlapping. Mirrors the
  // backend's assertNoOverlappingWorkPlan; existing (pre-validation) data can
  // still contain conflicts, which is exactly what this flags.
  const overlappingPlanIds = useMemo(() => {
    const conflicted = new Set<number>();
    const plans = workPlansData ?? [];
    const effectiveLocation = (plan: WorkPlanDto) =>
      plan.locationId ?? user.therapistLocationId ?? null;
    const overlaps = (a: WorkPlanDto, b: WorkPlanDto) => {
      const aStartsBeforeBEnds =
        !b.validityEnd || !a.validityStart || moment(a.validityStart).isBefore(b.validityEnd);
      const bStartsBeforeAEnds =
        !a.validityEnd || !b.validityStart || moment(b.validityStart).isBefore(a.validityEnd);
      return aStartsBeforeBEnds && bStartsBeforeAEnds;
    };
    for (let i = 0; i < plans.length; i++) {
      for (let j = i + 1; j < plans.length; j++) {
        if (effectiveLocation(plans[i]) !== effectiveLocation(plans[j])) continue;
        if (overlaps(plans[i], plans[j])) {
          if (plans[i].id != null) conflicted.add(plans[i].id!);
          if (plans[j].id != null) conflicted.add(plans[j].id!);
        }
      }
    }
    return conflicted;
  }, [workPlansData, user.therapistLocationId]);

  const handleWorkPlanClick = async (workPlan: WorkPlanInclusive) => {
    setSelectedWorkPlan(workPlan);
    // console.log("workPlan clicked", workPlan);
    return onItemClick && onItemClick(workPlan);
  };

  const handleAddNewRow = () => {
    setIsAddingRow(true);
    setPlanLocationId(user.therapistLocationId ?? null);
  };

  const handleDeleteRow = (workPlan: WorkPlanInclusive) => {
    setWorkPlanToDelete(workPlan);
  };

  /**
   * Handle clicking the edit button on a work plan.
   * Always allows direct inline editing of validity dates in this table.
   * Versioning is handled by WeeklyUserCalendar/WeeklyWorkDays when editing
   * working hours (Arbeitstag) for plans with historical data.
   */
  const handleEditRow = (workPlan: WorkPlanInclusive) => {
    startInlineEditing(workPlan);
  };

  /** Start inline editing for a work plan (validity dates only) */
  const startInlineEditing = (workPlan: WorkPlanInclusive) => {
    setEditingId(workPlan.id!);
    setPlanLocationId(workPlan.locationId ?? user.therapistLocationId ?? null);

    // Parse the formatted dates and convert to moment objects
    let start = moment(workPlan.validityStart, DISPLAY_DATE_FORMAT);
    if (!start.isValid()) start = moment(workPlan.validityStart); // try parsing as ISO

    let end = null;
    if (workPlan.validityEndInclusive !== "unbefristet") {
      end = moment(workPlan.validityEndInclusive, DISPLAY_DATE_FORMAT);
      if (!end.isValid()) end = moment(workPlan.validityEndInclusive); // try parsing as ISO
    }

    setStartDate(start.isValid() ? start : null);
    setEndDate(end?.isValid() ? end : null);
  };

  const handleConfirmDelete = async (id: number) => {
    try {
      await deleteWorkPlanMutation.mutateAsync({ workPlanId: id, userId: user.id });
      setIsAddingRow(false);
      if (selectedWorkPlan?.id === id) {
        setSelectedWorkPlan(undefined);
      }
      // Tell the parent as well. Clearing only this component's selection left
      // the calendar and the Arbeitstag list mounted on the plan that was just
      // deleted, still showing its rows and hours total (MD-351).
      onDeleted?.({ id } as WorkPlanDto);
    } finally {
      setWorkPlanToDelete(null);
    }
  };

  const handleCloseDialog = () => {
    // Close the confirmation dialog without deleting
    setWorkPlanToDelete(null);
  };

  const handleDuplicateRow = (workPlan: WorkPlanInclusive) => {
    setWorkPlanToDuplicate(workPlan);
  };

  const handleCloseDuplicateDialog = () => {
    setWorkPlanToDuplicate(null);
  };

  const handleConfirmDuplicate = async (validityStart: string) => {
    if (!workPlanToDuplicate || !weeklyWorkDaysForDuplicate) return;

    try {
      await versionWorkPlanMutation.mutateAsync({
        workPlanId: workPlanToDuplicate.id!,
        employeeId: user.id,
        effectiveDate: validityStart,
        weeklyWorkDays: weeklyWorkDaysForDuplicate,
      });
      setWorkPlanToDuplicate(null);
    } catch (error) {
      console.error("Error duplicating work plan:", error);
    }
  };

  const handleAddWorkPlan = async () => {
    if (!startDate) return;

    try {
      const workPlanIncl: WorkPlanInclusive = {
        employeeId: user.id,
        validityStart: startDate.format(),
        validityEndInclusive: endDate ? endDate.format() : null,
        locationId: planLocationId,
      };

      const workPlan = convertInclusiveToWP(workPlanIncl);
      await createWorkPlanMutation.mutateAsync(workPlan);
      setIsAddingRow(false);
      setStartDate(null);
      setEndDate(null);
      setPlanLocationId(null);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCancelClick = () => {
    setIsAddingRow(false);
    setStartDate(null);
    setEndDate(null);
    setPlanLocationId(null);
  };

  const handleSaveEdit = async (workPlan: WorkPlanInclusive) => {
    if (!startDate) return;

    try {
      const workPlanDto: WorkPlanDto = {
        id: workPlan.id!,
        employeeId: user.id,
        validityStart: moment(startDate)
          .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
          .format(),
        validityEnd: endDate
          ? moment(endDate)
              .add(1, "days")
              .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
              .format()
          : null,
        locationId: planLocationId,
      };

      await updateWorkPlanMutation.mutateAsync(workPlanDto);
      setEditingId(null);
      setStartDate(null);
      setEndDate(null);
      setPlanLocationId(null);
    } catch (error) {
      console.error(error);
    }
  };

  const normalizeToMoment = (date: DatePickerChangeEvent): Moment | null => {
    if (date instanceof Date) return moment(date);
    if (date?.target?.value) return moment(date.target.value);
    return null;
  };

  const handleEditStartDateChange = (date: DatePickerChangeEvent) => {
    const newDate = normalizeToMoment(date);
    if (newDate?.isValid()) {
      setStartDate(newDate);
    }
  };

  const handleEditEndDateChange = (date: DatePickerChangeEvent) => {
    if (!date) {
      setEndDate(null);
      return;
    }
    const newDate = normalizeToMoment(date);
    setEndDate(newDate?.isValid() ? newDate : null);
  };

  const handleCreateStartDateChange = (date: DatePickerChangeEvent) => {
    setStartDate(normalizeToMoment(date));
  };

  const handleCreateEndDateChange = (date: DatePickerChangeEvent) => {
    setEndDate(normalizeToMoment(date));
  };

  return (
    <>
      <FlatCard title="Arbeitsplan">
        {loading && <LoadingSpinner />}
        {/* Toggle for showing expired plans */}
        {expiredWorkPlans.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => setShowExpired(!showExpired)}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <History fontSize="small" />
              {showExpired
                ? "Frühere Arbeitspläne ausblenden"
                : `Frühere Arbeitspläne anzeigen (${expiredWorkPlans.length})`}
            </Link>
          </Box>
        )}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {workplanColumns.map((column) => (
                  <TableCell key={column.id}>{column.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {workPlans.map((workPlan) => {
                const isEditing = editingId === workPlan.id;
                const isExpired = expiredWorkPlans.some((p) => p.id === workPlan.id);

                return (
                  <TableRow
                    key={workPlan.id}
                    hover
                    sx={{
                      cursor: "pointer",
                      ...(isExpired && {
                        opacity: 0.6,
                        backgroundColor: (theme) => theme.palette.action.hover,
                      }),
                      ...(workPlan.id != null &&
                        overlappingPlanIds.has(workPlan.id) && {
                          backgroundColor: (theme) => theme.palette.warning.light + "40",
                        }),
                    }}
                    onClick={() => handleWorkPlanClick(workPlan)}
                    selected={selectedWorkPlan?.id === workPlan.id}
                  >
                    <TableCell>{workPlan.id}</TableCell>

                    {isEditing ? (
                      <>
                        {/* Edit mode */}
                        <TableCell colSpan={3}>
                          <Grid container spacing={2}>
                            <Grid item>
                              <Controls.DatePicker
                                label="Startdatum"
                                value={startDate?.format("YYYY-MM-DD")}
                                onChange={handleEditStartDateChange}
                                maxDate={endDate ? endDate.format("YYYY-MM-DD") : undefined}
                              />
                            </Grid>
                            <Grid item>
                              <Controls.DatePicker
                                label="Enddatum"
                                value={endDate?.format("YYYY-MM-DD")}
                                onChange={handleEditEndDateChange}
                                minDate={startDate ? startDate.format("YYYY-MM-DD") : undefined}
                              />
                            </Grid>
                            <Grid item sx={{ minWidth: 160 }}>
                              <Controls.Select
                                name="planLocationId"
                                label="Standort"
                                value={locationName(planLocationId)}
                                onChange={(e: any) => setPlanLocationId(e.target.id || null)}
                                options={locationSelectOptions}
                              />
                            </Grid>
                          </Grid>
                        </TableCell>
                        <TableCell>
                          <Grid container spacing={2}>
                            <Grid item>
                              <Button onClick={() => handleSaveEdit(workPlan)}>
                                <Save />
                                Speichern
                              </Button>
                            </Grid>
                            <Grid item>
                              <Button
                                onClick={() => {
                                  setEditingId(null);
                                  setStartDate(null);
                                  setEndDate(null);
                                  setPlanLocationId(null);
                                }}
                              >
                                <Clear />
                                Abbrechen
                              </Button>
                            </Grid>
                          </Grid>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        {/* Read mode */}
                        <TableCell>{workPlan.validityStart}</TableCell>
                        <TableCell>{workPlan.validityEndInclusive}</TableCell>
                        <TableCell>
                          <Grid container alignItems="center" spacing={1} wrap="nowrap">
                            {workPlan.id != null && overlappingPlanIds.has(workPlan.id) && (
                              <Grid item sx={{ display: "flex" }}>
                                <Tooltip title="Gültigkeitszeitraum überschneidet sich mit einem anderen Arbeitsplan an diesem Standort">
                                  <WarningAmber color="warning" fontSize="small" />
                                </Tooltip>
                              </Grid>
                            )}
                            <Grid item>
                              {workPlan.locationId
                                ? locationName(workPlan.locationId)
                                : `${locationName(user.therapistLocationId)} (Standard)`}
                            </Grid>
                          </Grid>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", flexWrap: "nowrap", gap: 1 }}>                        
                              <Button onClick={() => handleEditRow(workPlan)} size="small">
                                <Edit />
                              </Button>                         
                            <Tooltip title="Arbeitsplan duplizieren">
                              <Button
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDuplicateRow(workPlan);
                                }}
                              >
                                <ContentCopy />
                              </Button>
                            </Tooltip>
                            <Button
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteRow(workPlan);
                              }}
                            >
                              <Delete />
                            </Button>
                          </Box>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })}
              {/* New Row for Adding */}
              {isAddingRow && (
                <TableRow>
                  <TableCell>*</TableCell>
                  <TableCell>
                    <Controls.DatePicker
                      value={startDate}
                      onChange={handleCreateStartDateChange}
                      maxDate={endDate ? endDate.format("YYYY-MM-DD") : undefined}
                    />
                  </TableCell>
                  <TableCell>
                    <Controls.DatePicker
                      value={endDate}
                      onChange={handleCreateEndDateChange}
                      minDate={startDate ? startDate.format("YYYY-MM-DD") : undefined}
                    />
                  </TableCell>
                  <TableCell>
                    <Controls.Select
                      name="planLocationId"
                      label="Standort"
                      value={locationName(planLocationId)}
                      onChange={(e: any) => setPlanLocationId(e.target.id || null)}
                      options={locationSelectOptions}
                    />
                  </TableCell>
                  <TableCell>
                    <Grid container spacing={2}>
                      <Grid item>
                        <Button onClick={handleAddWorkPlan}>
                          <Save />
                          Speichern
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button onClick={handleCancelClick}>
                          <Clear />
                          Abbrechen
                        </Button>
                      </Grid>
                    </Grid>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!isAddingRow && (
          <Button onClick={handleAddNewRow} sx={{ mt: 2 }}>
            <Add />
            Neu
          </Button>
        )}
      </FlatCard>
      <DeleteConfirmationDialog
        open={workPlanToDelete !== null}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmDelete}
        id={workPlanToDelete?.id!}
      />
      <EffectiveDateDialog
        open={workPlanToDuplicate !== null}
        onClose={handleCloseDuplicateDialog}
        onConfirm={handleConfirmDuplicate}
        loading={versionWorkPlanMutation.isPending}
        mode="duplicate"
      />
    </>
  );
};

export default WorkPlans;
